'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import type { Theme } from '@/lib/theme/theme-matchers';

type ThemeContextValue = {
  theme: Theme;
  setTheme: (nextTheme: Theme) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

type ThemeProviderProps = {
  initialTheme: Theme;
  children: React.ReactNode;
};

export function ThemeProvider({ initialTheme, children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(initialTheme);

  const setTheme = useCallback((nextTheme: Theme) => {
    setThemeState((current) => (current === nextTheme ? current : nextTheme));
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    root.dataset.theme = theme;
  }, [theme]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme,
    }),
    [theme, setTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeController() {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useThemeController must be used within a ThemeProvider');
  }

  return context;
}
